import React, {BaseSyntheticEvent, useCallback, useMemo, useReducer, useState} from 'react';
import cx from 'classnames';

import {ActionPlanImpactLevel, CategoryKeyEnum} from '#apollo/schema';
import {Button, Icon, Text} from '#component/base';
import {ModalRoot} from '#component/base/Modal';
import {ActionPlanFilters} from '#component/modal/ActionPlanFilterModal/ActionPlanFilterModal.types';
import {ActionPlanItemDrawer} from '#component/modal/ActionPlanItemDrawer';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {RecommendationInput} from '#component/page/EditActionPlan/EditActionPlan.types';
import {GoalsBody} from '#component/partial/ActionPlanGoals/components/GoalsBody';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {RecommendationRow} from '../RecommendationRow';
import {
  getFilteredRecommendations,
  getSortedRecommendations,
  initialState,
  reducer,
} from './RecommendationTable.util';

import {SortType} from './RecommendationTable.types';

import * as css from './RecommendationTable.css';

const columns = [
  {sortType: 'title', title: 'Goals'},
  {sortType: 'category', title: 'Category'},
  {sortType: 'impactLevel', title: 'Impact for you'},
  {title: 'Learn Why'},
] as {title: React.ReactNode; sortType?: SortType}[];

export interface RecommendationTableProps extends React.ComponentPropsWithoutRef<'table'> {
  recommendations?: ActionPlanPage.ActionPlanAdjustment[];
  surveySubmissionId: string;
  filters: ActionPlanFilters;
  recommendationInput: RecommendationInput[];
  onChangeSelection?: (selection: RecommendationInput[]) => void;
}
export const RecommendationTable = ({
  className,
  recommendations = [],
  surveySubmissionId,
  filters,
  recommendationInput,
  onChangeSelection = () => {},
  ...otherProps
}: RecommendationTableProps) => {
  const {isDesktop} = useViewportSize();
  const [modalGoalOpened, setModalGoalOpened] = useState<boolean>(false);
  const [selectedGoal, setSelectedGoal] = useState<ActionPlanPage.ActionPlanAdjustment>();
  const [{sortDirection, sortType}, dispatch] = useReducer(reducer, initialState);

  const filteredRecommendations = useMemo(
    () => getFilteredRecommendations(filters, recommendations),
    [filters, recommendations],
  );
  const sortedAndFilteredRecommendations = useMemo(
    () => getSortedRecommendations(filteredRecommendations, sortDirection, sortType),
    [filteredRecommendations, sortDirection, sortType],
  );

  const handleSelectionChange = (
    e?: BaseSyntheticEvent,
    recommendationId?: string,
    healthFactorId?: string,
  ) => {
    if (e && recommendationId) {
      e.stopPropagation();
      onChangeSelection([
        ...recommendationInput.filter(({id}) => id !== recommendationId),
        {
          id: recommendationId,
          healthFactorId: healthFactorId || '',
          selected: !!e.target.checked,
        },
      ]);
    } else {
      const isSelected = recommendationInput.find(({id}) => id === selectedGoal?.id)?.selected;
      onChangeSelection([
        ...recommendationInput.filter(({id}) => id !== selectedGoal?.id),
        {
          id: selectedGoal?.id || '',
          healthFactorId: selectedGoal?.healthFactorId || '',
          selected: !isSelected,
        },
      ]);
    }
  };

  const handleItemClick = (recommendation: ActionPlanPage.ActionPlanAdjustment) => {
    if (isDesktop) return;
    setModalGoalOpened(true);
    setSelectedGoal(recommendation);
  };

  const handleLearnWhyClick = (recommendation: ActionPlanPage.ActionPlanAdjustment) => {
    setModalGoalOpened(true);
    setSelectedGoal(recommendation);
  };

  const [visibleRowCount, setVisibleRowCount] = useState<number>(10);
  const onClickLoadMore = useCallback(() => {
    setVisibleRowCount(prev => prev + 10);
  }, []);

  return (
    <>
      <table className={cx(css.root, className)} {...otherProps}>
        <thead>
          <tr>
            {columns
              .filter((_, i) => (isDesktop ? true : i === 0))
              .map(({sortType: columnSortType, title}, i) => {
                const hasSortTitle = !!columnSortType;
                const active = columnSortType === sortType;

                return (
                  <Text
                    key={i}
                    tabIndex={hasSortTitle ? 0 : undefined}
                    onClick={
                      hasSortTitle && isDesktop
                        ? () => dispatch({type: 'SetSortType', payload: columnSortType})
                        : undefined
                    }
                    as="th"
                    color="dustyGray"
                    className={sprinkles({p: [8, 16, 24]})}
                    textAlign="start"
                    variant="label">
                    <div className={sprinkles({flex: 'flex', items: 'center', gap: 8})}>
                      {title}

                      {hasSortTitle && isDesktop && (
                        <Icon
                          aria-label={`Current sort direction: ${
                            sortDirection === 'asc' ? 'Ascending' : 'Descending'
                          }`}
                          color="currentColor"
                          className={css.sortIcon({
                            active,
                            sortDirection: active ? sortDirection : undefined,
                          })}
                          name="CaretDown"
                        />
                      )}
                    </div>
                  </Text>
                );
              })}
          </tr>
        </thead>

        <tbody>
          {sortedAndFilteredRecommendations
            .filter((_, i) => i < visibleRowCount)
            .map(recommendation => (
              <RecommendationRow
                categoryKey={
                  recommendation.healthFactorAssessment?.categoryAssessment?.category?.key ||
                  CategoryKeyEnum.Research
                }
                key={recommendation.id}
                {...recommendation}
                selected={recommendationInput.find(({id}) => id === recommendation.id)?.selected}
                onChange={(e: BaseSyntheticEvent) =>
                  handleSelectionChange(e, recommendation.id, recommendation.healthFactorId || '')
                }
                onClick={() => handleItemClick(recommendation)}
                onClickLearnWhy={() => handleLearnWhyClick(recommendation)}
              />
            ))}
        </tbody>
      </table>
      {visibleRowCount < sortedAndFilteredRecommendations.length && (
        <div
          className={sprinkles({
            flex: 'flex',
            w: 'full',
            mT: [12, 12, 24],
            pX: [4, 4, 0],
            placeContent: 'center',
          })}>
          <Button
            variant="secondary"
            className={sprinkles({
              w: ['full', 'full', 'maxContent'],
            })}
            onClick={onClickLoadMore}>
            Load More
          </Button>
        </div>
      )}
      <ModalRoot open={modalGoalOpened} onOpenChange={() => setModalGoalOpened(false)}>
        {selectedGoal && (
          <ActionPlanItemDrawer
            title={selectedGoal?.healthFactorAssessment?.healthFactor?.title || ''}
            subtitle={selectedGoal?.recommendedResponse}
            categoryKey={
              selectedGoal?.healthFactorAssessment?.categoryAssessment?.category?.key ??
              CategoryKeyEnum.Research
            }
            impactLevel={selectedGoal?.impactLevel || undefined}
            imageUrl={selectedGoal?.healthFactorAssessment?.healthFactor?.imageUrl || ''}
            onAdd={handleSelectionChange}
            onRemove={handleSelectionChange}
            selected={recommendationInput.find(({id}) => id === selectedGoal?.id)?.selected}>
            <GoalsBody
              surveySubmissionId={surveySubmissionId}
              healthFactorAssessment={selectedGoal?.healthFactorAssessment}
              benefits={selectedGoal?.healthFactorAssessment?.healthFactor?.benefits ?? ''}
              tips={selectedGoal?.healthFactorAssessment?.healthFactor?.tips ?? ''}
              impactLevel={selectedGoal?.impactLevel as unknown as ActionPlanImpactLevel}
              variant="drawer"
            />
          </ActionPlanItemDrawer>
        )}
      </ModalRoot>
    </>
  );
};
